import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import {
  createOrder,
  listOrderFormBootstrap,
  searchSkusWithAvailability,
} from "@/lib/orders-edit.functions";
import { runServerFn } from "@/lib/server-fn";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SkuCombobox } from "@/components/orders/sku-combobox";
import { CustomFieldsForm, type CustomFieldDef } from "@/components/custom-fields/dynamic-fields";
import { listCustomFields } from "@/lib/custom-fields.functions";
import { validateCustomFields } from "@/lib/orders/custom-field-validate";

export const Route = createFileRoute("/_authenticated/app/orders/new")({
  component: NewOrderPage,
});

type Line = {
  key: string;
  sku_id: string;
  sku_code?: string;
  description?: string;
  qty_ordered: number;
  available?: number;
  unit_price?: number;
  hazmat: boolean;
};

function newLine(): Line {
  return { key: crypto.randomUUID(), sku_id: "", qty_ordered: 1, hazmat: false };
}

function NewOrderPage() {
  const navigate = useNavigate();
  const { tenantId, facilityId, customerId } = useScope();

  const bootstrapFn = useServerFn(listOrderFormBootstrap);
  const searchSkusFn = useServerFn(searchSkusWithAvailability);
  const createOrderFn = useServerFn(createOrder);

  const { data: boot } = useQuery({
    queryKey: ["order-form-bootstrap", tenantId],
    enabled: !!tenantId,
    queryFn: () => bootstrapFn({}),
  });

  const tenantCustomers = useMemo(
    () => (boot?.customers ?? []).filter((c: any) => c.tenant_id === tenantId),
    [boot, tenantId],
  );
  const tenantFacilities = useMemo(
    () => (boot?.facilities ?? []).filter((f: any) => f.tenant_id === tenantId),
    [boot, tenantId],
  );
  const tenantCarriers = useMemo(
    () => (boot?.carriers ?? []).filter((c: any) => c.tenant_id === tenantId),
    [boot, tenantId],
  );

  // Form state
  const [selCustomer, setSelCustomer] = useState<string>(customerId ?? "");
  const [selFacility, setSelFacility] = useState<string>(facilityId ?? "");
  const [orderNumber, setOrderNumber] = useState("");
  const [orderDate] = useState(new Date().toISOString());
  const [reqShipDate, setReqShipDate] = useState("");
  const [customerPO, setCustomerPO] = useState("");
  const [priority, setPriority] = useState<number>(5);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [source, setSource] = useState<"manual" | "ecom" | "b2b" | "edi" | "api" | "portal">("manual");

  // Contact
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [accountId, setAccountId] = useState("");

  // Addresses
  const [ship, setShip] = useState({ name: "", company: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", phone: "" });
  const [billSame, setBillSame] = useState(true);
  const [bill, setBill] = useState({ ...ship });

  // Carrier
  const [selCarrier, setSelCarrier] = useState("");
  const [selService, setSelService] = useState("");
  const carrierServices = useMemo(
    () => tenantCarriers.find((c: any) => c.code === selCarrier)?.services ?? [],
    [tenantCarriers, selCarrier],
  );

  // Notes
  const [notes, setNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");

  // Custom fields (per-tenant declared schema for `orders`)
  const listCustomFieldsFn = useServerFn(listCustomFields);
  const { data: cfData } = useQuery({
    queryKey: ["custom-fields-defs", "orders", tenantId],
    enabled: !!tenantId,
    queryFn: () => listCustomFieldsFn({ data: { tenantId: tenantId!, entity: "orders", enabledOnly: true } }),
  });
  const customFieldDefs = (cfData?.rows ?? []) as CustomFieldDef[];
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>({});
  const customFieldErrors = useMemo(
    () => validateCustomFields(customFieldDefs, customFieldValues),
    [customFieldDefs, customFieldValues],
  );

  // Lines
  const [lines, setLines] = useState<Line[]>([newLine()]);

  // Allocation summary
  const allocSummary = useMemo(() => {
    let short = 0, partial = 0, ok = 0;
    for (const l of lines) {
      if (!l.sku_id) continue;
      const avail = l.available ?? 0;
      if (avail >= l.qty_ordered) ok++;
      else if (avail > 0) partial++;
      else short++;
    }
    return { ok, partial, short };
  }, [lines]);

  const hasHazmat = lines.some((l) => l.hazmat);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [allowBackorder, setAllowBackorder] = useState(false);
  const [allocateNow, setAllocateNow] = useState(false);

  function addTag() {
    const t = tagInput.trim();
    if (!t || tags.includes(t)) return;
    setTags([...tags, t]);
    setTagInput("");
  }

  function pickSku(line: Line, sku: any) {
    setLines((ls) => ls.map((l) => l.key === line.key ? {
      ...l, sku_id: sku.id, sku_code: sku.sku_code, description: sku.description,
      available: sku.available,
    } : l));
  }

  async function onSubmit() {
    if (!tenantId || !selCustomer || !selFacility) {
      return toast.error("Select tenant, customer, and facility");
    }
    const cleanLines = lines.filter((l) => l.sku_id && l.qty_ordered > 0);
    if (cleanLines.length === 0) return toast.error("Add at least one line item");

    const cfErrs = Object.values(customFieldErrors);
    if (cfErrs.length > 0) return toast.error(cfErrs[0]);

    if (!allowBackorder && allocateNow && allocSummary.short + allocSummary.partial > 0) {
      return toast.error("Some lines are short on inventory. Enable 'Allow backorder' or remove them.");
    }

    setSubmitting(true);
    try {
      const res = await runServerFn(() => createOrderFn({ data: {
        tenant_id: tenantId,
        customer_id: selCustomer,
        facility_id: selFacility,
        order_number: orderNumber.trim(),
        order_date: orderDate,
        requested_ship_date: reqShipDate || undefined,
        customer_po: customerPO || undefined,
        priority,
        tags,
        ship_to: ship,
        billing_to: billSame ? ship : bill,
        customer_contact: {
          name: contactName, email: contactEmail, phone: contactPhone, account_id: accountId || undefined,
        },
        carrier: selCarrier || undefined,
        service_level: selService || undefined,
        notes: notes || undefined,
        internal_notes: internalNotes || undefined,
        source,
        status: allocateNow ? "pending_allocation" : "draft",
        custom_fields: Object.keys(customFieldValues).length > 0 ? customFieldValues : undefined,
        lines: cleanLines.map((l) => ({
          sku_id: l.sku_id,
          qty_ordered: l.qty_ordered,
          description: l.description,
          unit_price: l.unit_price,
          hazmat: l.hazmat,
        })),
      } }), "Create order");
      toast.success("Order created");
      navigate({ to: "/app/orders/$orderId", params: { orderId: res.orderId } });
    } catch { /* toast handled */ }
    finally { setSubmitting(false); }
  }

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") { e.preventDefault(); onSubmit(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 px-4 sm:px-6 py-4">
      {/* Main column */}
      <div className="space-y-4 min-w-0">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">New Order</h2>
            <p className="text-sm text-muted-foreground">Manual entry · ecommerce import · B2B</p>
          </div>
          <Link to="/app/orders" className="text-sm text-muted-foreground hover:underline">← All orders</Link>
        </div>

        <Card>
          <CardHeader><CardTitle>Customer & Facility</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Customer *</Label>
              <Select value={selCustomer} onValueChange={setSelCustomer}>
                <SelectTrigger><SelectValue placeholder="Choose customer" /></SelectTrigger>
                <SelectContent>
                  {tenantCustomers.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Facility *</Label>
              <Select value={selFacility} onValueChange={setSelFacility}>
                <SelectTrigger><SelectValue placeholder="Choose facility" /></SelectTrigger>
                <SelectContent>
                  {tenantFacilities.map((f: any) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Source</Label>
              <Select value={source} onValueChange={(v) => setSource(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["manual","ecom","b2b","edi","api","portal"].map((s) =>
                    <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Order Header</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Order number *</Label>
              <Input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="SO-1001" />
            </div>
            <div className="space-y-1.5"><Label>Customer PO</Label>
              <Input value={customerPO} onChange={(e) => setCustomerPO(e.target.value)} />
            </div>
            <div className="space-y-1.5"><Label>Requested ship date</Label>
              <Input type="date" value={reqShipDate} onChange={(e) => setReqShipDate(e.target.value)} />
            </div>
            <div className="space-y-1.5"><Label>Priority (1-9)</Label>
              <Input type="number" min={1} max={9} value={priority} onChange={(e) => setPriority(parseInt(e.target.value || "5", 10))} />
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Tags</Label>
              <div className="flex gap-2">
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                  placeholder="Add tag and press Enter" />
                <Button type="button" variant="outline" onClick={addTag}>Add</Button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {tags.map((t) => (
                    <Badge key={t} variant="secondary" className="cursor-pointer"
                      onClick={() => setTags(tags.filter((x) => x !== t))}>
                      {t} ×
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Customer Contact</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Name *</Label>
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Account ID</Label>
              <Input value={accountId} onChange={(e) => setAccountId(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Email *</Label>
              <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Phone *</Label>
              <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Shipping Address</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(["name","company","line1","line2","city","state","postal_code","country","phone"] as const).map((k) => (
              <div key={k} className={`space-y-1.5 ${["line1","line2"].includes(k) ? "sm:col-span-2" : ""}`}>
                <Label className="capitalize">{k.replace("_", " ")}{["name","line1","city","state","postal_code","country"].includes(k) ? " *" : ""}</Label>
                <Input value={(ship as any)[k]} onChange={(e) => setShip({ ...ship, [k]: e.target.value })} />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>Billing Address</CardTitle>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={billSame} onCheckedChange={(v) => setBillSame(!!v)} />
              Same as shipping
            </label>
          </CardHeader>
          {!billSame && (
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(["name","line1","city","state","postal_code","country"] as const).map((k) => (
                <div key={k} className="space-y-1.5">
                  <Label className="capitalize">{k.replace("_", " ")}</Label>
                  <Input value={(bill as any)[k]} onChange={(e) => setBill({ ...bill, [k]: e.target.value })} />
                </div>
              ))}
            </CardContent>
          )}
        </Card>

        <Card>
          <CardHeader><CardTitle>Line items *</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2 pr-2 w-[280px]">SKU</th>
                    <th className="text-left pr-2">Description</th>
                    <th className="text-right pr-2 w-20">Qty</th>
                    <th className="text-right pr-2 w-20">Avail</th>
                    <th className="text-right pr-2 w-24">Unit $</th>
                    <th className="text-center pr-2 w-16">Hazmat</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => {
                    const shortage = l.sku_id && (l.available ?? 0) < l.qty_ordered;
                    return (
                      <tr key={l.key} className="border-b">
                        <td className="py-2 pr-2">
                          <SkuCombobox
                            tenantId={tenantId}
                            facilityId={selFacility || undefined}
                            value={l.sku_id || undefined}
                            valueLabel={l.sku_code ? `${l.sku_code}` : undefined}
                            onSelect={(sku) => pickSku(l, sku)}
                          />
                        </td>
                        <td className="pr-2 text-muted-foreground truncate max-w-[200px]">{l.description ?? ""}</td>
                        <td className="pr-2"><Input type="number" min={1} value={l.qty_ordered}
                          className="h-8 text-right"
                          onChange={(e) => setLines((ls) => ls.map((x) => x.key === l.key
                            ? { ...x, qty_ordered: Math.max(1, parseInt(e.target.value || "1", 10)) } : x))} /></td>
                        <td className={`pr-2 text-right ${shortage ? "text-destructive font-medium" : ""}`}>
                          {l.sku_id ? (l.available ?? 0) : "—"}
                        </td>
                        <td className="pr-2"><Input type="number" min={0} step="0.01" value={l.unit_price ?? ""}
                          className="h-8 text-right"
                          onChange={(e) => setLines((ls) => ls.map((x) => x.key === l.key
                            ? { ...x, unit_price: e.target.value ? parseFloat(e.target.value) : undefined } : x))} /></td>
                        <td className="text-center"><Checkbox checked={l.hazmat}
                          onCheckedChange={(v) => setLines((ls) => ls.map((x) => x.key === l.key
                            ? { ...x, hazmat: !!v } : x))} /></td>
                        <td><Button type="button" size="icon" variant="ghost"
                          onClick={() => setLines((ls) => ls.length > 1 ? ls.filter((x) => x.key !== l.key) : [newLine()])}>
                          <Trash2 className="h-4 w-4" /></Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, newLine()])}>
              <Plus className="h-4 w-4 mr-1" />Add line
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Shipping Method</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Carrier *</Label>
              <Select value={selCarrier} onValueChange={(v) => { setSelCarrier(v); setSelService(""); }}>
                <SelectTrigger><SelectValue placeholder="Choose carrier" /></SelectTrigger>
                <SelectContent>
                  {tenantCarriers.map((c: any) => <SelectItem key={c.id} value={c.code}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Service type *</Label>
              <Select value={selService} onValueChange={setSelService} disabled={!selCarrier}>
                <SelectTrigger><SelectValue placeholder="Choose service" /></SelectTrigger>
                <SelectContent>
                  {carrierServices.map((s: any) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Customer-visible notes</Label>
              <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-warning">Internal warehouse notes</Label>
              <Textarea rows={4} className="bg-warning/5"
                value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} /></div>
          </CardContent>
        </Card>

        {customFieldDefs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Custom Fields</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <CustomFieldsForm
                defs={customFieldDefs}
                values={customFieldValues}
                onChange={setCustomFieldValues}
              />
              {Object.entries(customFieldErrors).length > 0 && (
                <ul className="text-xs text-destructive list-disc pl-4 space-y-0.5">
                  {Object.entries(customFieldErrors).map(([k, msg]) => (
                    <li key={k}>{msg}</li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sticky right rail */}
      <div className="lg:sticky lg:top-4 lg:self-start space-y-3">
        <Card>
          <CardHeader><CardTitle>Order Summary</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Lines</span><span>{lines.filter((l) => l.sku_id).length}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total units</span>
              <span>{lines.reduce((a, l) => a + (l.sku_id ? l.qty_ordered : 0), 0)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span>
              <span>${lines.reduce((a, l) => a + (l.unit_price ?? 0) * l.qty_ordered, 0).toFixed(2)}</span></div>
            {hasHazmat && (
              <div className="rounded-md bg-warning/10 px-2 py-1 text-warning text-xs flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Hazmat — verify carrier accepts
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Allocation</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-success" />
              <span>{allocSummary.ok} line(s) available</span></div>
            {allocSummary.partial > 0 && (
              <div className="flex items-center gap-2 text-warning">
                <AlertTriangle className="h-4 w-4" />{allocSummary.partial} partial</div>
            )}
            {allocSummary.short > 0 && (
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-4 w-4" />{allocSummary.short} short — will backorder</div>
            )}
            <label className="flex items-center gap-2 pt-2 text-xs">
              <Checkbox checked={allocateNow} onCheckedChange={(v) => setAllocateNow(!!v)} />
              Allocate immediately on save
            </label>
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={allowBackorder} onCheckedChange={(v) => setAllowBackorder(!!v)} />
              Allow backorder
            </label>
          </CardContent>
        </Card>

        <div className="space-y-2">
          <Button className="w-full" onClick={onSubmit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save order
          </Button>
          <p className="text-xs text-muted-foreground text-center">Ctrl/⌘+Enter to save</p>
        </div>
      </div>
    </div>
  );
}